import dayjs from 'dayjs';
import type { TreatmentCard, UsageItem } from '../Treatment/data';

/**
 * Nghiệp vụ demo phòng Kế toán: bill chuẩn đọc từ POS, khóa sổ 3 tầng, đối soát Payoo / sao kê,
 * hóa đơn đầu ra, thuế suất theo mã và ngày hiệu lực. Toàn bộ là hàm thuần, ghi dữ liệu chưa qua API.
 */

// ===================== Danh mục =====================

/** 3 tầng của SRS: quầy lập và sửa bill, kế toán nội bộ khóa sổ, kế toán trưởng mở khóa */
export type Tier = 'counter' | 'revenue' | 'chief';
export const TIER: Record<Tier, { label: string; short: string }> = {
  counter: { label: 'Tầng 1: Kế toán quầy, Sale Offline', short: 'Kế toán quầy' },
  revenue: { label: 'Tầng 2: Kế toán nội bộ bán hàng', short: 'Kế toán nội bộ' },
  chief: { label: 'Tầng 3: Kế toán trưởng', short: 'Kế toán trưởng' },
};

export type PayMethod = 'cash' | 'transfer' | 'vietqr' | 'payoo' | 'installment' | 'prepaid';
export const PAY_METHOD: Record<PayMethod, string> = {
  cash: 'Tiền mặt', transfer: 'Chuyển khoản', vietqr: 'VietQR', payoo: 'POS Payoo', installment: 'Trả góp Payoo', prepaid: 'Ví trả trước (POS)',
};
/** Tiền mặt, ví trả trước không có mã; các hình thức khác bắt buộc có mã giao dịch để đối soát */
export const needsRef = (m: PayMethod) => m !== 'cash' && m !== 'prepaid';
/** Mã phải lưu trên bill để đối soát tiền về (SRS 2.1) */
export const REF_LABEL: Record<PayMethod, string> = {
  cash: 'Không cần mã', prepaid: 'Không cần mã', transfer: 'Mã giao dịch chuyển khoản', vietqr: 'Mã giao dịch chuyển khoản', payoo: 'Số chuẩn chi POS', installment: 'Mã Payoo',
};
const isPayoo = (m: PayMethod) => m === 'payoo' || m === 'installment';
const isBankIn = (m: PayMethod) => m === 'transfer' || m === 'vietqr';

export type TaxClass = 'cosmetic' | 'medical' | 'retail';
export const TAX_CLASS: Record<TaxClass, string> = {
  cosmetic: 'Dịch vụ thẩm mỹ', medical: 'Dịch vụ y tế', retail: 'Sản phẩm bán lẻ',
};
/** Thuế suất gắn theo từng mã dịch vụ/sản phẩm (SRS 1.4); rate = null là không chịu thuế GTGT */
export interface TaxRate { id: string; serviceId: string; rate: number | null; from: string; note?: string }

export type ServiceType = 'service' | 'product' | 'package';
export const SERVICE_TYPE: Record<ServiceType, string> = { service: 'Dịch vụ', product: 'Sản phẩm', package: 'Gói liệu trình' };

export interface Service {
  id: string; name: string; taxClass: TaxClass;
  /** Tên đã đăng ký giá với Sở Y tế; rỗng là chưa đăng ký */
  registeredName: string;
  /** Mã đồng bộ với POS */
  code: string;
  type: ServiceType;
  group: string;
  /** Giá niêm yết đã có VAT */
  price: number;
  /** Định mức vật tư để cảnh báo thiếu tồn trước khi xuất hóa đơn (SRS 1.8) */
  bom?: { code: string; qty: number }[];
}

/** Thông tin người mua trên hóa đơn; không có là khách lẻ không lấy hóa đơn đích danh */
export interface Buyer { name: string; taxCode: string; address: string; email: string }

export interface BillLine {
  id: string; serviceId: string; name: string; gross: number;
  /** Giá niêm yết và phần chiết khấu đã bóc tách về dòng này (SRS 2.4) */
  list?: number; lineDiscount?: number; orderDiscount?: number;
  /** Ca thực hiện của dòng này: ekip, mã máy, số shot (SRS 3.3) */
  team?: Team; machine?: string; shots?: number;
}
export type TeamRole = 'doctor' | 'assistant' | 'nurse' | 'ktv';
export type Team = Partial<Record<TeamRole, string>>;
export interface Payment {
  id: string; method: PayMethod; amount: number; ref: string;
  /** Ngày thu nếu khác ngày bill (thu nợ) */
  date?: string;
}
export interface BillRefund { id: string; at: number; date: string; amount: number; method: PayMethod; ref: string; reason?: string }
export interface BillLog { at: number; by: string; action: string; reason?: string; changes?: { field: string; from: string; to: string }[] }
export type InvoiceStatus = 'none' | 'issued' | 'needs_adjust' | 'adjusted' | 'replaced';
export interface BillInvoice {
  status: InvoiceStatus; no?: string; at?: number; adjustNo?: string; replaceNo?: string;
  /** Lý do bill thay đổi sau khi đã xuất hóa đơn */
  flagReason?: string;
}
/** demo: tình huống mẫu · manual: lập tay · pos: đơn POS trên máy · server: đơn POS trên server */
export type BillSource = 'demo' | 'manual' | 'pos' | 'server';
/** Cảnh báo đơn POS đổi sau khi sổ đã khóa: bill giữ nguyên, Kế toán xử lý */
export interface PosAlert { id: string; at: number; orderId: string; text: string }
export interface PosSync { at?: number; orders: number; serverAt?: number; serverOrders: number; serverError?: string }

export interface Bill {
  id: string;
  /** Số chứng từ liên tục, bill hủy vẫn giữ số */
  seq: number;
  no: string;
  date: string;
  at: number;
  customer: string;
  phone: string;
  branchId: string;
  source: BillSource;
  /** Mã đơn bên POS */
  posOrderId?: string;
  buyer?: Buyer;
  /** Tiền còn nợ theo đơn POS */
  debt: number;
  refunds: BillRefund[];
  kind: 'service' | 'card' | 'deposit' | 'surcharge';
  /** Bill dùng dịch vụ gộp từ đơn cọc gốc */
  depositOf?: string;
  /** Phiếu phụ thu tham chiếu bill chính, nhưng sửa hủy độc lập */
  parentBillId?: string;
  /** Chương trình bán hàng của đơn cọc; hết chương trình thì hết hạn cọc */
  program?: { name: string; until: string };
  /** Hạn cọc do Kế toán trưởng gia hạn */
  depositUntil?: string;
  sale?: string;
  cskh?: string;
  team?: Team;
  lines: BillLine[];
  payments: Payment[];
  status: 'valid' | 'cancelled';
  createdBy: string;
  log: BillLog[];
  invoice: BillInvoice;
}

export interface PayooRow { ref: string; gross: number; fee: number; net: number; date: string }
export interface BankRow { date: string; amount: number; desc: string }
export interface DayLock { by: string; at: number }
export interface LockEvent { date: string; action: 'lock' | 'unlock'; by: string; at: number; reason?: string }

/** Một dòng nhật ký trừ thẻ: số riêng, không dùng chung chuỗi số bill thu tiền */
export interface UsageEntry {
  /** Trùng mã lượt bên Liệu trình */
  id: string; seq: number; no: string; cardId: string; date: string; at: number; items: UsageItem[];
  team: Team;
  /** Mã máy công nghệ cao để tính tua và khấu hao */
  machine: string;
  shots?: number;
  cancelled?: string;
  /** Lúc làm khách chưa trả đủ tiền thẻ */
  debtAtUse: boolean;
  overrideBy?: string;
}

export type CaseStatus = 'pending_l1' | 'pending_l2' | 'approved' | 'paid' | 'rejected';
export interface CaseStep { tier: Tier; by: string; at: number; action: string; note?: string }
/** Hồ sơ hủy thẻ, hoàn tiền: duyệt 2 cấp rồi mới chi */
export interface RefundCase {
  id: string; no: string; cardId: string; billId?: string; at: number; reason: string; status: CaseStatus;
  paid: number; consumed: number; refundable: number; penaltyPct: number; penalty: number; testFee: number; posFee: number; net: number;
  steps: CaseStep[]; method?: PayMethod; ref?: string; paidDate?: string;
}
export interface CommissionRate { group: string; sale: number; cskh: number; doctor: number; assistant: number; nurse: number; ktv: number }
/** full: phải trả đủ tiền thẻ mới được trừ buổi · paid: được dùng trong số tiền đã đóng */
export type CardGate = 'full' | 'paid';

// ---------- Kế toán quầy, Sale Offline ----------

export type CounterRole = 'cashier' | 'sale_offline';
export const COUNTER_ROLE: Record<CounterRole, string> = { cashier: 'Kế toán quầy', sale_offline: 'Sale Offline' };
export interface CounterUser { id: string; name: string; role: CounterRole }
/** Danh sách demo, bản thật lấy từ tài khoản đăng nhập */
export const COUNTER_USERS: CounterUser[] = [
  { id: 'kt-thu', name: 'Thu Demo', role: 'cashier' },
  { id: 'kt-van', name: 'Vân Demo', role: 'cashier' },
  { id: 'sale-em', name: 'Sale Demo Em', role: 'sale_offline' },
  { id: 'sale-giang', name: 'Sale Demo Giang', role: 'sale_offline' },
];
/** Sale Offline chỉ hỗ trợ sửa, hủy bill từ giờ này hoặc khi quầy bật chế độ đông khách */
export const SALE_SUPPORT_FROM = '17:30';

export interface DraftLine { id: string; serviceId: string; name: string; list: number; lineDiscount: number }
/** Hóa đơn tạm tính: chưa cấp số bill nên khách đổi ý thì bỏ, không phát sinh bill hủy */
export interface BillDraft {
  id: string; no: string; at: number; updatedAt: number; by: string; customer: string; phone: string; kind: 'service' | 'deposit'; sale: string;
  lines: DraftLine[]; orderDiscount: number; payments: Payment[]; note: string;
  status: 'draft' | 'paid' | 'dropped'; billId?: string; reason?: string;
}

export interface ShiftTxn { id: string; billId: string; billNo: string; customer: string; date: string; method: PayMethod; amount: number; ref: string; kind: 'receipt' | 'refund' }
export interface Shift {
  id: string; no: string; date: string; name: string; by: string; at: number; openingCash: number; txns: ShiftTxn[];
  cashExpected: number; cashCounted: number; diff: number; diffReason: string;
  /** closed: đã chốt và gửi Kế toán nội bộ · received: đã nhận · reopened: đã mở lại, giao dịch về ca sau */
  status: 'closed' | 'received' | 'reopened';
  receivedBy?: string; receivedAt?: number; reopenedBy?: string; reopenedAt?: number; reopenReason?: string;
}

export interface KtState {
  v: 5;
  tier: Tier;
  rates: TaxRate[];
  services: Service[];
  bills: Bill[];
  locks: Record<string, DayLock>;
  lockLog: LockEvent[];
  payoo: PayooRow[];
  bank: BankRow[];
  invoiceSeq: number;
  /** Tồn theo mã sản phẩm / vật tư */
  stock: Record<string, number>;
  rules: MappingRule[];
  vouchers: Voucher[];
  inputInvoices: InputInvoice[];
  posAlerts: PosAlert[];
  posSync: PosSync;
  cards: TreatmentCard[];
  usageLog: UsageEntry[];
  refundCases: RefundCase[];
  commissionRates: CommissionRate[];
  cardGate: CardGate;
  /** Hạn cọc mặc định khi đơn cọc không gắn chương trình */
  depositDays: number;
  /** Tỷ lệ phí cà thẻ dùng khi chưa có file đối soát */
  posFeePct: number;
  /** Người đang thao tác ở quầy */
  counterUser: string;
  /** Ngày quầy bật chế độ đông khách để Sale Offline hỗ trợ sửa, hủy trước 17h30 */
  rushDays: Record<string, { by: string; at: number }>;
  drafts: BillDraft[];
  shifts: Shift[];
  /** Quỹ tiền mặt đầu ca mặc định */
  cashFloat: number;
}

// ===================== Sổ: phạm vi, bảng mapping, chứng từ =====================

export type BookScope = 'both' | 'tax' | 'mgmt';
/** Dòng định khoản do Kế toán trưởng thiết lập; forVoucher = chọn được khi lập chứng từ tay */
export interface MappingRule { id: string; label: string; debit: string; credit: string; scope: BookScope; forVoucher?: boolean; note?: string }
export interface Voucher {
  id: string; no: string; date: string; ruleId: string; desc: string; amount: number; scope: BookScope;
  /** Bắt buộc khi chứng từ chỉ nằm ở một sổ: dùng cho báo cáo giải trình chênh lệch */
  reason: string;
  costCenter: string; branchId: string; by: string; at: number;
  /** Chi phí không được trừ khi quyết toán TNDN (SRS 1.6) */
  nonDeductible?: string;
}
export type InputPay = 'cash' | 'bank' | 'none';
export const INPUT_PAY: Record<InputPay, string> = { cash: 'Tiền mặt', bank: 'Không dùng tiền mặt', none: 'Chưa thanh toán' };
/** Hóa đơn mua vào: giá trị gồm VAT từ 5 triệu cần chứng từ thanh toán không dùng tiền mặt để khấu trừ */
export interface InputInvoice { id: string; no: string; seller: string; taxCode: string; date: string; net: number; vat: number; rate: number; debit: string; pay: InputPay; payRef: string; desc: string }

// ===================== Tiện ích =====================

const nf = new Intl.NumberFormat('vi-VN');
export const fmtMoney = (n: number) => `${nf.format(Math.round(n))} ₫`;
export const ymd = (t: number) => dayjs(t).format('YYYY-MM-DD');
export const dmy = (d: string) => dayjs(d).format('DD/MM/YYYY');
export const billNo = (seq: number) => `BL-${String(seq).padStart(5, '0')}`;
const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
const normRef = (r: string) => r.trim().toUpperCase();

export const billTotal = (b: Pick<Bill, 'lines'>) => sum(b.lines.map(l => l.gross));
export const paidTotal = (b: Pick<Bill, 'payments'>) => sum(b.payments.map(p => p.amount));
export const billById = (s: Pick<KtState, 'bills'>, id?: string) => s.bills.find(b => b.id === id) || null;
/** Bill bán hàng có hoa hồng: không tính cọc, phiếu phụ thu */
export const isSale = (b: Pick<Bill, 'kind'>) => b.kind === 'service' || b.kind === 'card';

// ===================== Thuế suất theo ngày hiệu lực =====================

/** Thuế suất đang hiệu lực của một mã tại một ngày: dòng có ngày hiệu lực gần nhất không vượt quá ngày đó */
export function rateOn(rates: TaxRate[], serviceId: string, date: string): TaxRate | null {
  return rates.filter(r => r.serviceId === serviceId && r.from <= date).sort((a, b) => b.from.localeCompare(a.from))[0] || null;
}

/** Bảng giá lưu giá đã có VAT: tách ngược ra giá chưa thuế */
export function splitVat(gross: number, rate: number | null) {
  if (!rate) return { net: gross, vat: 0 };
  const net = Math.round(gross / (1 + rate / 100));
  return { net, vat: gross - net };
}

export const rateLabel = (rate: number | null | undefined) => (rate === null || rate === undefined ? 'Không chịu thuế' : `${rate}%`);

export function billTax(s: Pick<KtState, 'rates'>, b: Pick<Bill, 'lines' | 'date'>) {
  let net = 0; let vat = 0;
  b.lines.forEach(l => {
    const r = rateOn(s.rates, l.serviceId, b.date);
    const v = splitVat(l.gross, r?.rate ?? null);
    net += v.net; vat += v.vat;
  });
  return { net, vat };
}

// ===================== Bill: sửa, hủy, kiểm tra số chứng từ =====================

export const isLocked = (s: Pick<KtState, 'locks'>, date: string) => !!s.locks[date];
/** Ngày đã từng khóa và đang được kế toán trưởng mở lại */
export const isReopened = (s: Pick<KtState, 'locks' | 'lockLog'>, date: string) =>
  !s.locks[date] && s.lockLog.some(e => e.date === date && e.action === 'unlock');

/** Ca đã chốt có chứa giao dịch của bill này */
export const closedShiftOf = (s: Partial<Pick<KtState, 'shifts'>>, b: Pick<Bill, 'id'>) => (s.shifts || []).find(x => x.status !== 'reopened' && x.txns.some(t => t.billId === b.id)) || null;

/** Lý do không được sửa/hủy bill; null là được phép */
export function modifyBlock(s: Pick<KtState, 'locks' | 'lockLog'> & Partial<Pick<KtState, 'shifts' | 'rushDays'>>, b: Bill, tier: Tier, today: string, actor?: { role: CounterRole; hm: string }): string | null {
  if (b.status === 'cancelled') return 'Bill đã hủy';
  if (b.source === 'pos' || b.source === 'server') return 'Bill từ POS: sửa hoặc hủy trên POS, Kế toán tự nhận thay đổi';
  if (isLocked(s, b.date)) return tier === 'chief' ? 'Sổ đã khóa: mở khóa ngày này trước khi cho sửa' : 'Sổ đã khóa, chỉ Kế toán trưởng mở được';
  if (tier !== 'counter') return 'Chỉ Kế toán quầy, Sale Offline được sửa/hủy bill';
  if (b.date !== today && !isReopened(s, b.date)) return 'Chỉ được sửa/hủy trong ngày làm việc';
  if (actor?.role === 'sale_offline' && actor.hm < SALE_SUPPORT_FROM && !s.rushDays?.[today]) return 'Sale Offline chỉ hỗ trợ sửa/hủy sau 17h30 hoặc khi quầy bật chế độ đông khách';
  const shift = isReopened(s, b.date) ? null : closedShiftOf(s, b);
  if (shift) return `${shift.no} đã chốt: Kế toán nội bộ mở lại ca rồi mới sửa/hủy được`;
  return null;
}

export interface BillPatch { lines: BillLine[]; payments: Payment[] }

export function validatePatch(p: BillPatch, reason: string): string[] {
  const errors: string[] = [];
  if (reason.trim().length < 5) errors.push('Nhập lý do (tối thiểu 5 ký tự)');
  if (p.lines.some(l => !(l.gross > 0))) errors.push('Số tiền dịch vụ phải lớn hơn 0');
  if (billTotal(p) !== paidTotal(p)) errors.push(`Tổng thanh toán ${fmtMoney(paidTotal(p))} khác tiền bill ${fmtMoney(billTotal(p))}`);
  p.payments.forEach(x => { if (needsRef(x.method) && !x.ref.trim()) errors.push(`${PAY_METHOD[x.method]}: bắt buộc nhập mã giao dịch`); });
  return errors;
}

/** Bill đã xuất hóa đơn mà bị sửa/hủy thì tự báo để lập hóa đơn điều chỉnh */
export const flagInvoice = (inv: BillInvoice, flagReason?: string): BillInvoice => (
  inv.status === 'issued' || inv.status === 'adjusted' || inv.status === 'replaced' ? { ...inv, status: 'needs_adjust', flagReason } : inv
);

/** Áp dụng sửa bill và ghi nhật ký giá trị cũ/mới */
export function applyEdit(b: Bill, p: BillPatch, by: string, reason: string, at: number): Bill {
  const changes: { field: string; from: string; to: string }[] = [];
  p.lines.forEach(l => {
    const old = b.lines.find(x => x.id === l.id);
    if (old && old.gross !== l.gross) changes.push({ field: `Tiền ${l.name}`, from: fmtMoney(old.gross), to: fmtMoney(l.gross) });
  });
  p.payments.forEach(x => {
    const old = b.payments.find(y => y.id === x.id);
    if (!old) return;
    if (old.amount !== x.amount) changes.push({ field: `Thu ${PAY_METHOD[x.method]}`, from: fmtMoney(old.amount), to: fmtMoney(x.amount) });
    if (old.ref !== normRef(x.ref)) changes.push({ field: 'Mã giao dịch', from: old.ref || '(trống)', to: normRef(x.ref) });
    if (old.method !== x.method) changes.push({ field: 'Hình thức', from: PAY_METHOD[old.method], to: PAY_METHOD[x.method] });
  });
  return {
    ...b,
    lines: p.lines,
    payments: p.payments.map(x => ({ ...x, ref: normRef(x.ref) })),
    invoice: changes.length ? flagInvoice(b.invoice, `Sửa bill: ${reason.trim()}`) : b.invoice,
    log: [...b.log, { at, by, action: 'Sửa bill', reason: reason.trim(), changes }],
  };
}

/** Hủy không xóa: giữ nguyên số để chuỗi chứng từ không đứt đoạn */
export function applyCancel(b: Bill, by: string, reason: string, at: number): Bill {
  const changes = [{ field: 'Trạng thái', from: 'Hợp lệ', to: 'Đã hủy' }, { field: 'Tiền bill', from: fmtMoney(billTotal(b)), to: fmtMoney(0) }];
  return { ...b, status: 'cancelled', invoice: flagInvoice(b.invoice, `Hủy bill: ${reason.trim()}`), log: [...b.log, { at, by, action: 'Hủy bill', reason: reason.trim(), changes }] };
}

/** Kiểm tra số chứng từ: không đứt đoạn, không nhảy lùi ngày */
export function checkSequence(bills: Bill[]): string[] {
  const sorted = [...bills].sort((a, b) => a.seq - b.seq);
  const issues: string[] = [];
  sorted.forEach((b, i) => {
    if (!i) return;
    const prev = sorted[i - 1];
    if (b.seq !== prev.seq + 1) issues.push(`Đứt số: thiếu từ ${billNo(prev.seq + 1)} đến ${billNo(b.seq - 1)}`);
    if (b.date < prev.date) issues.push(`${b.no} ngày ${dmy(b.date)} nằm sau ${prev.no} ngày ${dmy(prev.date)}`);
  });
  return issues;
}

// ===================== Khóa sổ doanh thu =====================

export function dayTotals(s: Pick<KtState, 'bills' | 'rates'>, date: string) {
  const all = s.bills.filter(b => b.date === date);
  const valid = all.filter(b => b.status === 'valid');
  const tax = valid.map(b => billTax(s, b));
  return {
    all, valid,
    gross: sum(valid.map(billTotal)),
    net: sum(tax.map(t => t.net)),
    vat: sum(tax.map(t => t.vat)),
    cancelled: all.length - valid.length,
    edited: all.filter(b => b.log.some(l => l.action === 'Sửa bill')).length,
  };
}

/** Điều kiện bấm "Duyệt khóa sổ": lỗi thì chặn */
export function dayCloseErrors(s: Pick<KtState, 'bills'> & Partial<Pick<KtState, 'usageLog'>>, date: string): string[] {
  const errors: string[] = [];
  const refCount = new Map<string, number>();
  s.bills.filter(b => b.status === 'valid').forEach(b => b.payments.forEach(p => { if (p.ref) refCount.set(normRef(p.ref), (refCount.get(normRef(p.ref)) || 0) + 1); }));
  s.bills.filter(b => b.date === date && b.status === 'valid').forEach(b => {
    b.payments.forEach(p => {
      if (needsRef(p.method) && !p.ref.trim()) errors.push(`${b.no}: thiếu ${REF_LABEL[p.method]} (${PAY_METHOD[p.method]})`);
      if (p.ref && (refCount.get(normRef(p.ref)) || 0) > 1) errors.push(`${b.no}: mã ${p.ref} trùng với bill khác`);
    });
    if (billTotal(b) !== paidTotal(b) + b.debt) errors.push(`${b.no}: tiền thu lệch tiền bill`);
  });
  (s.usageLog || []).filter(e => e.date === date && !e.cancelled && !Object.values(e.team).some(Boolean)).forEach(e => errors.push(`${e.no}: lượt trừ thẻ chưa có Bác sĩ, KTV hoặc Điều dưỡng`));
  return errors;
}

// ===================== Chốt ca =====================

type ShiftState = Pick<KtState, 'bills' | 'shifts' | 'locks' | 'lockLog'>;

/** Giao dịch chưa vào ca nào. Ngày đã khóa, hoặc đang được Kế toán trưởng mở lại, không tính vào ca mới */
export function pendingTxns(s: ShiftState): ShiftTxn[] {
  const closed = new Set(s.shifts.filter(x => x.status !== 'reopened').flatMap(x => x.txns.map(t => t.id)));
  const open = (date: string) => !s.locks[date] && !isReopened(s, date);
  return s.bills.filter(b => b.status === 'valid').flatMap(b => [
    ...b.payments.map((p): ShiftTxn => ({ id: p.id, billId: b.id, billNo: b.no, customer: b.customer, date: p.date || b.date, method: p.method, amount: p.amount, ref: p.ref, kind: 'receipt' })),
    ...b.refunds.map((r): ShiftTxn => ({ id: r.id, billId: b.id, billNo: b.no, customer: b.customer, date: r.date, method: r.method, amount: -r.amount, ref: r.ref, kind: 'refund' })),
  ]).filter(t => open(t.date) && !closed.has(t.id));
}

/** Điều kiện về ca khi khóa sổ: hết giao dịch chưa chốt, biên bản đã được Kế toán nội bộ nhận */
export function shiftCloseErrors(s: ShiftState, date: string): string[] {
  if (isReopened(s, date)) return [];
  const errors: string[] = [];
  const pending = pendingTxns(s).filter(t => t.date === date);
  if (pending.length) errors.push(`Còn ${pending.length} giao dịch chưa chốt ca`);
  s.shifts.filter(x => x.status === 'closed' && x.txns.some(t => t.date === date)).forEach(x => errors.push(`${x.no}: Kế toán nội bộ chưa nhận biên bản chốt ca`));
  return errors;
}

// ===================== Đối soát =====================

/** dup: dòng thứ hai trỏ vào giao dịch đã khớp với dòng khác (file xuất lặp, tiền về hai lần) — không ghi sổ */
export type MatchStatus = 'matched' | 'amount' | 'no_bill' | 'dup';
export const MATCH_LABEL: Record<MatchStatus, string> = { matched: 'Khớp', amount: 'Lệch số tiền', no_bill: 'Không có bill', dup: 'Trùng giao dịch' };

export interface PayooMatch { row: PayooRow; bill: Bill | null; status: MatchStatus; diff: number }

/** Khớp file Payoo với bill theo mã giao dịch; tách tiền thực nhận và phí. Mỗi giao dịch chỉ khớp một dòng */
export function matchPayoo(rows: PayooRow[], bills: Bill[]) {
  const pays = bills.filter(b => b.status === 'valid').flatMap(b => b.payments.filter(p => isPayoo(p.method)).map(p => ({ b, p })));
  const seen = new Set<string>();
  const matches: PayooMatch[] = rows.map(row => {
    const ref = normRef(row.ref);
    const all = pays.filter(x => normRef(x.p.ref) === ref);
    const hit = all.find(x => !seen.has(x.p.id));
    if (!hit) return all.length ? { row, bill: all[0].b, status: 'dup', diff: row.gross } : { row, bill: null, status: 'no_bill', diff: row.gross };
    seen.add(hit.p.id);
    const diff = row.gross - hit.p.amount;
    return { row, bill: hit.b, status: diff ? 'amount' : 'matched', diff };
  });
  return {
    matches,
    missing: pays.filter(x => !seen.has(x.p.id)),
    net: sum(rows.map(r => r.net)),
    fee: sum(rows.map(r => r.fee)),
    gross: sum(rows.map(r => r.gross)),
  };
}

export interface BankMatch { row: BankRow; bill: Bill | null; status: MatchStatus; diff: number; by: 'ref' | 'bill' | null }

/** Mã giao dịch ngắn hơn thế này dễ trùng một đoạn số trong nội dung chuyển khoản khác (SĐT, số TK) */
const MIN_REF_LEN = 6;
/** `code` đứng thành một cụm riêng trong nội dung (không dính chữ / số hai bên) */
const hasToken = (text: string, code: string) => new RegExp(`(^|[^A-Z0-9])${code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|[^A-Z0-9])`).test(text);

/** Đọc nội dung chuyển khoản: tìm mã giao dịch hoặc số bill trong chuỗi rồi khớp số tiền. Mỗi giao dịch chỉ khớp một dòng */
export function matchBank(rows: BankRow[], bills: Bill[]) {
  const pays = bills.filter(b => b.status === 'valid').flatMap(b => b.payments.filter(p => isBankIn(p.method)).map(p => ({ b, p })));
  const seen = new Set<string>();
  const matches: BankMatch[] = rows.map(row => {
    const text = normRef(row.desc);
    const refHits = pays.filter(x => normRef(x.p.ref).length >= MIN_REF_LEN && hasToken(text, normRef(x.p.ref)));
    const billHits = pays.filter(x => hasToken(text, x.b.no));
    const byRef = refHits.find(x => !seen.has(x.p.id));
    const hit = byRef || billHits.find(x => !seen.has(x.p.id));
    if (!hit) {
      const dupOf = refHits[0] || billHits[0];
      return dupOf ? { row, bill: dupOf.b, status: 'dup', diff: row.amount, by: refHits.length ? 'ref' : 'bill' } : { row, bill: null, status: 'no_bill', diff: row.amount, by: null };
    }
    seen.add(hit.p.id);
    const diff = row.amount - hit.p.amount;
    return { row, bill: hit.b, status: diff ? 'amount' : 'matched', diff, by: byRef ? 'ref' : 'bill' };
  });
  return { matches, missing: pays.filter(x => !seen.has(x.p.id)), total: sum(rows.map(r => r.amount)) };
}

const cell = (row: Record<string, unknown>, names: string[]) => {
  const key = Object.keys(row).find(k => names.includes(k.normalize('NFC').trim().toLowerCase()));
  return key === undefined ? undefined : row[key];
};
const num = (v: unknown) => (typeof v === 'number' ? v : Number(String(v ?? '').replace(/[^\d-]/g, '')) || 0);
const dateCell = (v: unknown) => {
  const t = String(v ?? '').trim();
  const m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  return m ? `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}` : t.slice(0, 10);
};

/** Đọc dòng từ file Excel đối soát Payoo; file khác định dạng sẽ cần thêm tên cột */
export function parsePayoo(rows: Record<string, unknown>[]): PayooRow[] {
  return rows.flatMap(r => {
    const ref = String(cell(r, ['mã giao dịch', 'ma giao dich', 'ref', 'mã gd']) ?? '').trim();
    if (!ref) return [];
    const gross = num(cell(r, ['số tiền', 'so tien', 'tiền giao dịch', 'gross']));
    const fee = num(cell(r, ['phí', 'phi', 'fee']));
    const netCell = cell(r, ['thực nhận', 'thuc nhan', 'net']);
    return [{ ref, gross, fee, net: netCell === undefined ? gross - fee : num(netCell), date: dateCell(cell(r, ['ngày', 'ngay', 'date'])) }];
  });
}

/** Đọc dòng tiền vào từ sao kê ngân hàng */
export function parseBank(rows: Record<string, unknown>[]): BankRow[] {
  return rows.flatMap(r => {
    const amount = num(cell(r, ['ghi có', 'ghi co', 'số tiền', 'so tien', 'credit']));
    if (amount <= 0) return [];
    return [{ amount, date: dateCell(cell(r, ['ngày', 'ngay', 'date'])), desc: String(cell(r, ['nội dung', 'noi dung', 'nội dung chuyển khoản', 'diễn giải', 'description']) ?? '') }];
  });
}

// ===================== Hóa đơn đầu ra =====================

/** Đơn cọc gốc đã gộp vào bill dùng dịch vụ */
export const depositBill = (s: Pick<KtState, 'bills'>, b: Bill) => (b.depositOf ? billById(s, b.depositOf) : null);
/** Giá trị hóa đơn = tiền bill + tiền cọc gốc gộp vào */
export const invoiceAmount = (s: Pick<KtState, 'bills'>, b: Bill) => billTotal(b) + (depositBill(s, b) ? billTotal(depositBill(s, b)!) : 0);

/** Các dòng lên hóa đơn: gồm cả dòng của đơn cọc gộp vào */
export function invoiceLines(s: Pick<KtState, 'bills'>, b: Bill) {
  const dep = depositBill(s, b);
  return dep ? [...b.lines, ...dep.lines] : b.lines;
}

/** VAT của hóa đơn: gồm cả tiền cọc gộp vào, tính theo ngày xuất là ngày dùng dịch vụ */
export function invoiceTax(s: Pick<KtState, 'bills' | 'rates'>, b: Bill) {
  return billTax(s, { date: b.date, lines: invoiceLines(s, b) });
}

/** Cọc chưa dùng: chưa có bill dùng dịch vụ nào gộp đơn cọc này */
export const openDeposits = (s: Pick<KtState, 'bills'>) =>
  s.bills.filter(b => b.kind === 'deposit' && b.status === 'valid' && !s.bills.some(x => x.depositOf === b.id && x.status === 'valid'));
export const depositsOf = (s: Pick<KtState, 'bills'>) => s.bills.filter(b => b.kind === 'deposit' && b.status === 'valid');

const phoneKey = (p: string) => p.replace(/\D/g, '');
/** Cọc chưa dùng của cùng khách (theo SĐT), có trước bill này: gợi ý Kế toán gộp vào hóa đơn */
export const depositsFor = (s: Pick<KtState, 'bills'>, b: Bill) => (b.depositOf || !isSale(b) || !phoneKey(b.phone) ? [] : openDeposits(s)
  .filter(d => phoneKey(d.phone) === phoneKey(b.phone) && d.at <= b.at));

/**
 * Hàng chờ xuất: mọi bill hợp lệ đều vào luồng hóa đơn, riêng cọc đợi đến khi khách dùng dịch vụ.
 * Khách không cần hóa đơn đích danh vẫn phải có hóa đơn, chỉ không có thông tin người mua.
 */
export const invoiceQueue = (s: Pick<KtState, 'bills'>) => s.bills.filter(b => b.status === 'valid' && b.kind !== 'deposit' && b.invoice.status === 'none');

export interface InvoiceCheck { errors: string[]; warnings: string[] }

/** Chặn hoặc cảnh báo trước khi phát hành (SRS 1.3, 1.8) */
export function invoiceCheck(s: Pick<KtState, 'bills' | 'services' | 'rates' | 'stock'>, b: Bill, today?: string): InvoiceCheck {
  const errors: string[] = [];
  const warnings: string[] = [];
  const need: Record<string, number> = {};
  invoiceLines(s, b).forEach(l => {
    const sv = s.services.find(x => x.id === l.serviceId);
    if (!sv) { errors.push(`${l.name}: chưa khai báo mã trong danh mục`); return; }
    if (!rateOn(s.rates, sv.id, b.date)) errors.push(`${l.name}: chưa khai báo thuế suất cho ngày ${dmy(b.date)}`);
    if (!sv.registeredName) errors.push(`${l.name}: chưa đăng ký giá với Sở Y tế`);
    else if (sv.registeredName !== l.name) warnings.push(`Hóa đơn sẽ ghi tên đã đăng ký: ${sv.registeredName}`);
    if (sv.type === 'product') need[sv.code] = (need[sv.code] || 0) + 1;
    sv.bom?.forEach(m => { need[m.code] = (need[m.code] || 0) + m.qty; });
  });
  // Thiếu tồn chỉ cảnh báo: khách đã mua thì vẫn phải có hóa đơn, kế toán kiểm tra chứng từ nhập
  Object.entries(need).forEach(([code, qty]) => {
    const have = s.stock[code];
    if (have !== undefined && have < qty) warnings.push(`${code}: cần ${qty}, tồn sổ còn ${have}. Kiểm tra chứng từ nhập kho`);
  });
  if (b.kind === 'card' || b.lines.some(l => s.services.find(x => x.id === l.serviceId)?.type === 'package')) warnings.push('Thẻ liệu trình: chờ Kế toán trưởng chốt thời điểm xuất hóa đơn');
  if (b.debt > 0) warnings.push(`Đơn còn nợ ${fmtMoney(b.debt)}`);
  if (b.buyer && !b.buyer.taxCode.trim()) warnings.push('Người mua là đơn vị nhưng thiếu mã số thuế');
  depositsFor(s, b).forEach(d => warnings.push(`Khách có cọc ${d.no} (${fmtMoney(billTotal(d))}) chưa dùng: kiểm tra có gộp không`));
  if (today && b.date < today) warnings.push(`Quá ngày lập: bill ngày ${dmy(b.date)} chưa có hóa đơn`);
  const dup = s.bills.find(x => x.id !== b.id && x.status === 'valid' && x.date === b.date && x.customer === b.customer && phoneKey(x.phone) === phoneKey(b.phone) && billTotal(x) === billTotal(b));
  if (dup) warnings.push(`Có thể trùng với ${dup.no}`);
  return { errors, warnings };
}

export const invoiceNo = (seq: number) => `HD-${String(seq).padStart(7, '0')}`;

// ===================== Hóa đơn mua vào =====================

export const NON_CASH_MIN = 5_000_000;

/** Cảnh báo khấu trừ: hóa đơn mua vào từ 5 triệu (gồm VAT) chưa có chứng từ không dùng tiền mặt; mua nhiều lần trong ngày cộng gộp để không lọt do chia nhỏ */
export function inputWarnings(list: InputInvoice[], inv: InputInvoice): string[] {
  const out: string[] = [];
  const gross = inv.net + inv.vat;
  if (inv.pay === 'bank' && !inv.payRef.trim()) out.push('Thiếu mã chứng từ thanh toán');
  if (inv.pay !== 'bank' && gross >= NON_CASH_MIN) out.push(`Từ 5 triệu mà ${INPUT_PAY[inv.pay].toLowerCase()}: chưa đủ điều kiện khấu trừ`);
  if (inv.pay !== 'bank' && gross < NON_CASH_MIN) {
    const sameDay = list.filter(x => x.date === inv.date && x.taxCode === inv.taxCode && x.pay !== 'bank');
    const total = sum(sameDay.map(x => x.net + x.vat));
    if (sameDay.length > 1 && total >= NON_CASH_MIN) out.push(`Cùng người bán trong ngày tổng ${fmtMoney(total)}: kiểm tra chia nhỏ hóa đơn`);
  }
  return out;
}
